import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Button, Table, Col, Form as BSForm, ButtonGroup, Dropdown, Stack } from '../ui-kit';
import { Formik, Form, FieldArray, useFormikContext } from 'formik';
import { TextField, Checkbox, Select, SubmitButton, NumberField } from '../general/forms/bootstrap-formik';
import { intersection, isEmpty, isEqual } from 'lodash';
import Loader from '../general/Loader';
import { IconArrowDown, IconArrowUp, IconTrash, IconPlus } from '../ui-kit/icons';
import createToast from '../../utils/toast';
import './OAPConfigurationForm.css';

import { operations as userOperations } from '../../state/modules/users';
import { selectors as userSelectors } from '../../state/modules/users';
import { operations as groupOperations } from '../../state/modules/groups';
import { selectors as groupSelectors } from '../../state/modules/groups';
import { operations as pagerConfigOperations } from '../../state/modules/pager-configurations';
import { selectors as pagerConfigSelectors } from '../../state/modules/pager-configurations';
import { operations as pagerTemplateOperations } from '../../state/modules/pager-templates';
import { selectors as pagerTemplateSelectors } from '../../state/modules/pager-templates';
import { usePrevious } from '../../utils/custom-hooks';
import { ButtonLoadingContent } from '../general/SaveButton';

const i18n = I18n.t('activemodel.attributes.mobile/pagers/swissphone_pager_configuration');
const bundleTypeOptions = I18n.t('classy_enum.mobile/pagers/swissphone_pager_configuration/bundle_type');

const messageTypeOptions = I18n.t('classy_enum.mobile/pagers/swissphone_pager_configuration/message_type');

// for end user input, don't allow OAP option
const { oap, ...messageTypeOptionsWithoutOap } = messageTypeOptions;

const checkboxStyle = { width: '16px', height: '16px' };
const selectStyle = { paddingLeft: '6px', paddingRight: '6px' };

const validateNotEmpty = value => {
  if (!value) return I18n.t('errors.messages.blank');
};

const validateCode = value => {
  if (!value) return I18n.t('errors.messages.blank');
  const codePattern = /^\d{1,7}$/;
  if (!codePattern.test(value)) {
    return 'code must be a number between 1 and 9999999';
  }
};

const validatePocsagCodeWithSubaddressOptional = value => {
  if (!value) return I18n.t('errors.messages.blank');
  const pocsagPattern = /^\d{1,7}[ABCD]?$/;
  if (!pocsagPattern.test(value)) {
    return 'POCSAG addresses must be a number between 1 and 9999999 with an optional subaddress (A, B, C, D)';
  }
};

// When using GSM+Radio or Radio channels, the subaddress is mandatory. Otherwise
// there could be an error when installing updates in the pager. This is why we
// have two different validation functions for the code field. The first one is for
// GSM only (optional subaddress) and this one for GSM+Radio and Radio (mandatory subaddress)
const validatePocsagCodeWithSubaddressMandatory = value => {
  if (!value) return I18n.t('errors.messages.blank');
  const pocsagPattern = /^\d{1,7}[ABCD]$/;
  if (!pocsagPattern.test(value)) {
    return 'POCSAG addresses for GSM+Radio and Radio must be a number between 1 and 9999999 and a mandatory subaddress (A, B, C, D)';
  }
};

// depending on the network type, we render different input fields for the Code.
// When pocsag, we allow 1234567A (address + subaddress). If Flex (or unknown),
// we allow numeric only.
const BundleCodeField = ({ name, readOnly, networkType, bundleType }) => {
  if (networkType === 'pocsag') {
    const validator =
      bundleType === 'gsm_and_radio' || bundleType === 'radio'
        ? validatePocsagCodeWithSubaddressMandatory
        : validatePocsagCodeWithSubaddressOptional;
    return <TextField name={name} disabled={readOnly} validate={validator} inputOnly />;
  } else {
    return <NumberField name={name} disabled={readOnly} validate={validateCode} inputOnly />;
  }
};

const canMoveUp = (values, index) => index !== 0 && !values[index - 1].read_only;
const canMoveDown = (values, index) => index < values.length - 1;

const swap = (form, values, index1, index2) => {
  const item = values[index1];
  values[index1] = values[index2];
  values[index2] = item;
  form.setTouched(true);
};
const moveUp = (form, values, index) => swap(form, values, index, index - 1);
const moveDown = (form, values, index) => swap(form, values, index, index + 1);

const ReorderButtons = ({ form, values, index }) => (
  <ButtonGroup size="sm" className="me-3">
    <Button onClick={() => moveUp(form, values, index)} disabled={!canMoveUp(values, index)}>
      <IconArrowUp />
    </Button>
    <Button onClick={() => moveDown(form, values, index)} disabled={!canMoveDown(values, index)}>
      <IconArrowDown />
    </Button>
  </ButtonGroup>
);

// The active profile checkbox has a uniqueness to it. It's value
// is either multi-select or single select based on the value of the
// type field. We need to do some post-processing to make sure there
// is only ever one active = true with type select profile
const ProfileActiveCheckbox = ({ name, styleOverrides }) => {
  const { values, setFieldValue } = useFormikContext();

  const previousValues = usePrevious(values);

  useEffect(() => {
    if (!previousValues || !values) return;

    const prevActiveSelectProfiles = previousValues.profiles.filter(
      profile => profile.active && profile.type === 'select'
    );

    const newActiveSelectProfiles = values.profiles.filter(profile => profile.active && profile.type === 'select');

    // Avoid infinite re-renders
    if (isEqual(prevActiveSelectProfiles, newActiveSelectProfiles)) return;
    // If there is only one active and select profile, then we are good
    // no need to change anything
    if (newActiveSelectProfiles.length <= 1) return;

    const profilesToDeactivate = intersection(newActiveSelectProfiles, prevActiveSelectProfiles);

    for (const profile of profilesToDeactivate) {
      setFieldValue(`profiles.${profile.id}.active`, false);
    }
  }, [values, previousValues, setFieldValue]);

  return <Checkbox name={name} style={styleOverrides} inputOnly />;
};

// This custom hook is used to fetch the plannable stations for a given user
// and group. It is used to populate the station-specific acknowledgment
// dropdown in the OAP Configuration form.
const usePlannableStationsById = (userId, fetchGroups, fetchUser) => {
  const usersById = useSelector(userSelectors.byId);
  const groupsById = useSelector(groupSelectors.byId);

  useEffect(() => {
    if (!userId) return;
    fetchUser({ params: { ids: userId } });
  }, [userId, fetchUser]);

  const user = usersById[userId];
  const plannableGroupIds = user?.memberships
    ?.filter(membership => membership.plannable)
    ?.map(membership => membership.group_id);

  useEffect(() => {
    if (!plannableGroupIds) return;
    fetchGroups({ params: { ids: plannableGroupIds } });
  }, [plannableGroupIds, fetchGroups]);

  const plannableGroups = plannableGroupIds?.map(id => groupsById[id])?.filter(Boolean);
  const plannableStationsById = {};
  plannableGroups
    ?.filter(group => group.type === 'station')
    ?.map(group => (plannableStationsById[group.id] = group.name));

  return plannableStationsById;
};

// This is a wrapper class that allows placing the OAP Configuration
// form in any part of the website, providing a standalone connection
// to redux
const OAPConfigurationForm = ({
  pagerId,
  fetchPagerConfiguration,
  fetchPagerTemplates,
  fetchUser,
  pagerConfigurationsByPagerId,
  pagerTemplates,
  updatePagerConfigurationFields,
  storePagerConfigurationInBackend,
  fetchGroups
}) => {
  useEffect(() => {
    fetchPagerConfiguration({ params: { id: pagerId } });
    fetchPagerTemplates({ params: { pager_id: pagerId, presentation_type: 'basic' } });
  }, [pagerId, fetchPagerConfiguration, fetchPagerTemplates]);

  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const pagerConfiguration = pagerConfigurationsByPagerId[pagerId];
  const userId = pagerConfiguration?.user_id;
  const plannableStationsById = {
    0: I18n.t('all'),
    ...usePlannableStationsById(userId, fetchGroups, fetchUser)
  };

  const [savingType, setSavingType] = useState('save');

  const fetchDefaultPagerConfiguration = () => fetchPagerConfigurationTemplate('default');

  const fetchPagerConfigurationTemplate = template_id => {
    setLoadingTemplate(true);

    fetchPagerConfiguration({ forceFetch: true, params: { id: pagerId, template_id } })
      .catch(_error => {
        createToast({
          title: I18n.t('error'),
          message: I18n.t('error_occurred'),
          type: 'error',
          delay: 5000
        });
      })
      .finally(() => setLoadingTemplate(false));
  };

  const renderProfilesForm = ({ name, form, remove, push }) => {
    const values = (form && form.values[name]) || [];

    const profileTypeOptions = { select: 'select', toggle: 'toggle' };

    const addProfile = () => {
      const nextId = isEmpty(values) ? 0 : Math.max(...values.map(value => value.id)) + 1;
      push({
        // Get the highest profile ID attached to this pager config and add 1 to it
        id: nextId,
        name: '',
        active: false,
        type: 'select'
      });
    };

    return (
      <>
        <Table>
          <thead>
            <tr>
              <th>{i18n.profiles.active}</th>
              <th>{i18n.profiles.name}</th>
              <th>{i18n.profiles.type}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {
              // When creating a FieldArray with Formik we need to take special care of
              // using the index set by Formik for the items since not doing it can
              // create duplication of indices when removing one of these items
              // (see: https://github.com/jaredpalmer/formik/issues/3393).
            }
            {values.map((profile, index) => (
              <tr key={index}>
                <td>
                  <ProfileActiveCheckbox name={`${name}.${index}.active`} styleOverrides={checkboxStyle} />
                </td>
                <td>
                  <TextField name={`${name}.${index}.name`} maxLength={21} validate={validateNotEmpty} inputOnly />
                </td>
                <td>
                  <Select name={`${name}.${index}.type`} options={profileTypeOptions} style={selectStyle} inputOnly />
                </td>
                <td>
                  <ReorderButtons form={form} values={values} index={index} />
                  <Button variant="danger" onClick={() => remove(index)}>
                    <IconTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button onClick={addProfile} className="mb-3">
          <IconPlus />
        </Button>
      </>
    );
  };

  const renderBundlesForm = ({ name, form, remove, push }) => {
    const values = (form && form.values[name]) || [];
    const profiles = (form && form.values.profiles) || [];

    const toneOptions = {};

    for (let x = 1; x <= 32; x++) {
      toneOptions[x] = x;
    }

    const addBundle = () => {
      push({
        name: '',
        code: '',
        type: 'gsm_and_radio',
        tone: '1',
        priority: false,
        confirmation: false
      });
    };

    return (
      <div style={{ overflowX: 'visible', margin: 'auto' }}>
        <Table className="pager-configuration-table" hover>
          <thead>
            <tr>
              <th scope="col">{i18n.bundles.number}</th>
              <th scope="col">{i18n.bundles.name}</th>
              <th scope="col">{i18n.bundles.code}</th>
              <th scope="col">{i18n.bundles.type}</th>
              {networkType === 'pocsag' && (
                <th scope="col" style={{ width: '9rem' }}>
                  {i18n.bundles.message_type}
                </th>
              )}
              <th scope="col">{i18n.bundles.tone}</th>
              <th scope="col" style={{ width: '5rem', textAlign: 'center' }}>
                {i18n.bundles.priority}
              </th>
              <th scope="col" style={{ width: '5rem', textAlign: 'center' }}>
                {i18n.bundles.confirmation}
              </th>
              {stationSpecificAcknowledgmentEnabled && (
                <th scope="col" style={{ width: '15rem' }}>
                  {i18n.bundles.confirmation_station_id}
                </th>
              )}
              {profiles.map(profile => (
                <th style={{ textAlign: 'center' }} key={profile.id}>
                  {profile.name}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {values.map((bundle, index) => (
              <tr key={index}>
                <td>
                  <BSForm.Group>
                    <BSForm.Control plaintext readOnly defaultValue={index + 1} />
                  </BSForm.Group>
                </td>
                <td>
                  <TextField
                    name={`${name}.${index}.name`}
                    maxLength={12}
                    disabled={bundle.read_only}
                    validate={validateNotEmpty}
                    inputOnly
                  />
                </td>
                <td>
                  <BundleCodeField
                    readOnly={bundle.read_only}
                    name={`${name}.${index}.code`}
                    networkType={networkType}
                    bundleType={bundle.type}
                  />
                </td>
                <td>
                  <Select
                    name={`${name}.${index}.type`}
                    options={bundleTypeOptions}
                    style={selectStyle}
                    disabled={bundle.read_only}
                    inputOnly
                  />
                </td>
                {networkType === 'pocsag' && (
                  <td>
                    <Select
                      name={`${name}.${index}.message_type`}
                      options={
                        // for end users, don't show OAP as an option
                        bundle.read_only ? messageTypeOptions : messageTypeOptionsWithoutOap
                      }
                      style={selectStyle}
                      disabled={bundle.read_only}
                      inputOnly
                    />
                  </td>
                )}
                <td>
                  <Select
                    name={`${name}.${index}.tone`}
                    options={toneOptions}
                    style={selectStyle}
                    disabled={bundle.read_only && !(bundle.writable_attrs || []).includes('tone')}
                    inputOnly
                  />
                </td>
                <td style={{ textAlign: '-webkit-center' }}>
                  <Checkbox
                    name={`${name}.${index}.priority`}
                    disabled={bundle.read_only}
                    style={checkboxStyle}
                    inputOnly
                  />
                </td>
                <td style={{ textAlign: '-webkit-center' }}>
                  <Checkbox
                    name={`${name}.${index}.confirmation`}
                    disabled={bundle.read_only}
                    style={checkboxStyle}
                    inputOnly
                  />
                </td>
                {stationSpecificAcknowledgmentEnabled && (
                  <td>
                    {bundle.confirmation && !bundle.read_only && (
                      <Select
                        name={`${name}.${index}.confirmation_station_id`}
                        options={plannableStationsById}
                        style={selectStyle}
                        inputOnly
                      />
                    )}
                  </td>
                )}
                {profiles.map(profile => (
                  <td key={profile.id} style={{ textAlign: '-webkit-center' }}>
                    <Checkbox
                      name={`${name}.${index}.profile_id_${profile.id}`}
                      disabled={bundle.read_only}
                      style={checkboxStyle}
                      inputOnly
                    />
                  </td>
                ))}
                <td>
                  {!bundle.read_only && (
                    <>
                      <ReorderButtons form={form} values={values} index={index} />
                      <Button variant="danger" onClick={() => remove(index)}>
                        <IconTrash />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button onClick={addBundle} className="mb-3">
          <IconPlus />
        </Button>
      </div>
    );
  };

  const savePagerConfiguration = (newConfig, options) => {
    // Before saving, we need to decode the bundles' profile IDs
    const pagerConfigBundlesDecoded = newConfig.bundles.map(bundle => {
      const newProfileIds = Object.entries(bundle)
        .filter(([key, value]) => key.includes('profile_id_') && value)
        .map(([key, _]) => Number.parseInt(key.split('_id_')[1]))
        .filter(id => newConfig.profiles.map(p => p.id).includes(id));

      return { ...bundle, profile_ids: newProfileIds };
    });

    const newConfigDecoded = { ...newConfig, bundles: pagerConfigBundlesDecoded };

    updatePagerConfigurationFields(pagerId, newConfigDecoded).then(() => {
      storePagerConfigurationInBackend(pagerId, options);
    });
  };

  if (!pagerConfiguration) return <Loader />;

  const networkType = pagerConfiguration.network_type;
  const stationSpecificAcknowledgmentEnabled = pagerConfiguration.station_specific_acknowledgment_enabled;

  // Encodes the bundle profile IDs so each can be controlled by an individual
  // checkbox. It looks like the following:
  // { ..., profile_ids: [1,3] } -> {..., profile_id_1: true, profile_id_2: false, profile_id_3: true}
  const pagerConfigBundlesEncoded = pagerConfiguration.bundles.map(bundle => {
    const profileIdsHashed = {};
    for (const profile of pagerConfiguration.profiles) {
      profileIdsHashed[`profile_id_${profile.id}`] = bundle.profile_ids.includes(profile.id);
    }
    return { ...bundle, ...profileIdsHashed };
  });

  const handleCloseSuccessfulMessage = () => location.reload();

  const pagerConfigurationEncoded = { ...pagerConfiguration, bundles: pagerConfigBundlesEncoded };

  const toastMessage = () => {
    const savingTypes = {
      default: i18n.messages.success,
      notify: i18n.messages.success_notify,
      force: i18n.messages.success_force
    };
    return savingTypes[savingType];
  };

  return (
    <Formik
      initialValues={pagerConfigurationEncoded}
      enableReinitialize={true}
      onSubmit={values => {
        if (savingType === 'default') {
          savePagerConfiguration(values);
        } else if (savingType === 'notify') {
          savePagerConfiguration(values, { params: { notify: true } });
        } else if (savingType === 'force') {
          savePagerConfiguration(values, { params: { force: true } });
        }
        createToast({
          title: I18n.t('saved'),
          message: toastMessage(),
          type: 'success',
          delay: 3000,
          onClose: handleCloseSuccessfulMessage
        });
      }}
    >
      <Form>
        <div style={{ minHeight: '110px' }}>
          <legend>
            {i18n.titles.general}

            <Dropdown as={ButtonGroup} className="float-right">
              <Button variant="success" size="sm" onClick={fetchDefaultPagerConfiguration}>
                {loadingTemplate ? <ButtonLoadingContent /> : I18n.t('pager.default_configuration')}
              </Button>
              {pagerTemplates.length > 0 && (
                <>
                  <Dropdown.Toggle split size="sm" variant="success" />
                  <Dropdown.Menu>
                    <Dropdown.Header>{I18n.t('pager.templates')}</Dropdown.Header>
                    {pagerTemplates.map(template => (
                      <Dropdown.Item
                        key={template.id}
                        onClick={() => fetchPagerConfigurationTemplate(template.id)}
                        title={template.description || ''}
                      >
                        {template.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </>
              )}
            </Dropdown>
          </legend>
          <BSForm.Group>
            <Col sm={2}>
              <BSForm.Label>{i18n.general.user_line}</BSForm.Label>
            </Col>
            <Col sm={6}>
              <TextField name={'user_line'} validate={validateNotEmpty} inputOnly />
            </Col>
          </BSForm.Group>
        </div>
        <br />
        <div>
          <legend> {i18n.titles.profiles}</legend>
          <FieldArray name="profiles" render={renderProfilesForm} />
        </div>
        <br />
        <div>
          <legend> {i18n.titles.bundles}</legend>
          <FieldArray name="bundles" render={renderBundlesForm} />
        </div>
        <Stack direction="horizontal" gap={2}>
          <SubmitButton variant="success" className="ms-auto" onClick={() => setSavingType('default')} />
          <SubmitButton
            variant="primary"
            text={I18n.t('pager.oap.save.notify')}
            onClick={() => setSavingType('notify')}
          />
          <SubmitButton
            variant="warning"
            text={I18n.t('pager.oap.save.force')}
            onClick={() => setSavingType('force')}
          />
        </Stack>
      </Form>
    </Formik>
  );
};

const mapStateToProps = state => ({
  pagerConfigurationsByPagerId: pagerConfigSelectors.byId(state),
  pagerTemplates: pagerTemplateSelectors.values(state)
});

const mapDispatchToProps = {
  fetchPagerConfiguration: pagerConfigOperations.fetch,
  updatePagerConfigurationFields: pagerConfigOperations.updateFields,
  storePagerConfigurationInBackend: pagerConfigOperations.storeInBackend,
  fetchPagerTemplates: pagerTemplateOperations.fetch,
  fetchUser: userOperations.fetch,
  fetchGroups: groupOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(OAPConfigurationForm);
